import { isString, toMilliseconds } from '@art-suite/art-core-ts';
import { DEFAULT_MAX_AGE_MS } from '../QueryCache';
import { CacheEntry, isCacheEntryExpired, TypedCacheEntry } from './CacheEntry';
import { PersistenceLayerBase, PersistenceLayerBaseOptions } from './PersistenceLayerBase';

export interface InMemoryPersistenceLayerOptions extends PersistenceLayerBaseOptions {
  initialPurgeInterval?: number;
  purgeTimeout?: number;
}

const PURGE_INTERVAL_COUNTDOWN = 100;

const DEFAULT_OPTIONS: InMemoryPersistenceLayerOptions = {
  defaultMaxAgeMs: DEFAULT_MAX_AGE_MS,
  initialPurgeInterval: PURGE_INTERVAL_COUNTDOWN,
  purgeTimeout: 100,
};
/**
 * In-memory persistence layer using Map for storage.
 * Filters out expired entries on all read operations.
 */
export class InMemoryPersistenceLayer<ValueType> extends PersistenceLayerBase<ValueType> {
  private storage = new Map<string, CacheEntry>();
  private autoPurgeCountdown = PURGE_INTERVAL_COUNTDOWN;
  private readonly inMemoryOptions: InMemoryPersistenceLayerOptions;

  constructor(options: InMemoryPersistenceLayerOptions = DEFAULT_OPTIONS) {
    super(options);
    this.inMemoryOptions = options;
    this.autoPurgeCountdown = this.inMemoryOptions.initialPurgeInterval ?? PURGE_INTERVAL_COUNTDOWN;
  }

  get defaultPurgeInterval() {
    return this.inMemoryOptions.initialPurgeInterval ?? PURGE_INTERVAL_COUNTDOWN;
  }

  get purgeTimeout() {
    return this.inMemoryOptions.purgeTimeout ?? 100;
  }

  async has(key: string, now?: number) {
    const entry = this.storage.get(key);
    return !!entry && !isCacheEntryExpired(entry, now);
  }

  async getEntry(key: string, now?: number, prefetchOnly?: boolean) {
    this.autoPurge();
    const entry = this.storage.get(key) as TypedCacheEntry<ValueType> | undefined;
    if (!entry) {
      return undefined;
    }
    if (isCacheEntryExpired(entry, now)) {
      this.storage.delete(key);
      return undefined;
    }
    if (prefetchOnly) {
      return { key, value: undefined, createdAt: entry.createdAt, expiresAt: entry.expiresAt };
    }
    return { key, value: entry.value, createdAt: entry.createdAt, expiresAt: entry.expiresAt };
  }

  async getMetadata(key: string, now?: number) {
    const entry = this.storage.get(key);
    if (!entry) {
      return undefined;
    }
    if (isCacheEntryExpired(entry, now)) {
      this.storage.delete(key);
      return undefined;
    }
    return {
      ...entry,
      value: undefined,
    };
  }

  async getCacheCount(): Promise<number> {
    return this.storage.size;
  }

  async getCacheSize(): Promise<number> {
    let byteSize = 0;
    for (const entry of this.storage.values()) {
      byteSize += isString(entry.value) ? entry.value.length : JSON.stringify(entry.value).length;
    }
    return byteSize;
  }

  async set(key: string, value: ValueType, expiresAt?: number, createdAt?: number, now?: number): Promise<TypedCacheEntry<ValueType>> {
    this.autoPurge();
    const cacheEntry: TypedCacheEntry<ValueType> = {
      key,
      value,
      createdAt: createdAt ?? toMilliseconds(now),
      expiresAt: this.getExpiresAt(expiresAt, now),
    };
    this.storage.set(key, cacheEntry);
    return cacheEntry;
  }

  async freshen(key: string, expiresAt?: number, now?: number): Promise<boolean> {
    this.autoPurge();
    const entry = await this.getEntry(key);
    if (entry?.value) {
      this.set(key, entry.value, this.getExpiresAt(expiresAt, now), entry.createdAt, now);
    }
    return !!entry;
  }

  async purge(key: string): Promise<boolean> {
    return this.storage.delete(key);
  }

  async getAllKeys(prefix?: string, now?: number) {
    this.autoPurge();
    const result: string[] = [];
    for (const [key, entry] of this.storage) {
      if (isCacheEntryExpired(entry, now)) {
        this.storage.delete(key);
        continue;
      }
      if (!prefix || key.startsWith(prefix)) {
        result.push(key);
      }
    }
    return result;
  }

  // *************************************************************************************
  // Private
  // *************************************************************************************
  private autoPurge() {
    this.autoPurgeCountdown--;
    if (this.autoPurgeCountdown === 0) {
      setTimeout(
        () => {
          this.purgeExpiredEntries();
          this.autoPurgeCountdown = this.storage.size; // on a growing cache, keeps autoPurge amortized to O(log n)
        },
        this.purgeTimeout, // make sure it happens out-of-band to avoid blocking the main thread
      );
    }
  }
}
